/**
 * HOW AN OVERDUE BALANCE IS WORDED beside a company name on the Accounts
 * dashboard. Pure, no React, no fetching — the one place this sentence exists.
 *
 * ```
 * Acme University (GBP 12,400.00 overdue, 45 days)
 * Boston University (GBP 12,400.00 + USD 3,000.00 overdue, 45 days)
 * ```
 *
 * ## 🔴 ONE BRACKET, EVERY CURRENCY LISTED SEPARATELY — owner's decision
 *
 * A company can be overdue in more than one currency at once, and **there is no
 * exchange-rate column anywhere in this system, by design** — invoices are
 * per-currency and never converted. So one figure over mixed currencies is
 * wrong by construction, and every shortcut to producing one is forbidden here:
 *
 * - **Never sum across currencies.** There is no rate to sum at.
 * - **Never pick a "main" currency.** Largest-by-digits is not largest-by-value
 *   across two currencies, and there is no way to find out which is.
 * - **Never drop the smaller.** A dropped figure is money nobody chases, and the
 *   understatement is invisible — a shorter bracket reads like a smaller debt,
 *   not like a missing line.
 *
 * The separator between currencies is **` + `**, and that is a decision rather
 * than punctuation. It is the sign that says *these are two quantities that have
 * not been added*. `joinList()` / `formatList()` from `@/lib/format` would give
 * "GBP 12,400.00 and USD 3,000.00", which reads as prose and invites the reader
 * to do the sum in their head — the exact conclusion the owner's ruling exists
 * to prevent. Do not "harmonise" this onto the shared list joiner.
 *
 * ## The cases, enumerated — every one of these is reachable
 *
 * | input | result |
 * | --- | --- |
 * | `totals` empty | **`null`** — the caller renders NOTHING. Never `( overdue, 45 days)` |
 * | one currency | `GBP 12,400.00 overdue, 45 days` |
 * | several currencies | `GBP 12,400.00 + USD 3,000.00 overdue, 45 days` |
 * | `daysOverdue` 1 | `… overdue, 1 day` — singular |
 * | `daysOverdue` 0, negative, fractional or not finite | **the days clause is DROPPED**: `GBP 12,400.00 overdue` |
 * | a currency code nobody recognises | printed **as given** |
 * | an amount of `'0.00'` | printed — `0.00` is a figure, not an absence |
 * | a row with a blank amount or a blank code | that ROW is skipped; if every row goes, the whole label is `null` |
 *
 * ## Money is a decimal string and never sees a float
 *
 * Amounts arrive as exact DECIMAL strings and go through
 * {@link formatDecimalString}. **Never `Number(amount)`** — `Number("18000.10")
 * * 100` is not `1801000`, and this figure is what somebody chases a customer
 * for. That also rules out sorting the rows by value, comparing one against
 * another, or testing an amount against zero: all three need arithmetic this
 * module deliberately cannot do.
 *
 * **The rows are rendered in the order the server sent them.** Same rule as a
 * line's `cities` and a kanban board's `columns`: they arrive ordered, and
 * re-sorting them here would be a second opinion about a sequence somebody else
 * owns — and any value-based sort would need the float this module refuses.
 *
 * ## A currency code is rendered, never resolved
 *
 * Print the code you were given, trimmed and otherwise verbatim. **Do not look
 * it up in an active-currency list and blank it when missing**: only 3 of 164
 * currency rows are active (measured 2026-08-31), reads never filter on
 * `isActive`, and a live document can legitimately name a retired one. A blanked
 * code turns a real debt into `12,400.00 overdue` against no currency at all.
 *
 * No case-folding either. The wire contract is `^[A-Z]{3}$` with no folding, so
 * anything else that arrives is the server's answer and not ours to correct —
 * upper-casing it would hide that something upstream is wrong.
 *
 * ## Never construct a `Date` from `oldestDueDate`
 *
 * `new Date('2026-05-31')` is midnight **UTC** and renders as 30 May for anyone
 * west of Greenwich. {@link formatDateOnly} is the house helper and builds the
 * date in UTC on both ends so it cannot drift; `todayIsoDate()` in
 * `@/lib/invoice-utils` is the matching "today from LOCAL parts", for the same
 * reason mirrored.
 *
 * ## ⚠️ NOTHING HERE DERIVES `daysOverdue`, DELIBERATELY
 *
 * The server's figure is used as given and no second one is computed, not even
 * as a cross-check. Two answers to one question is the problem, not the
 * solution: the viewer's clock is a *viewer's* clock — `/tasks` already records
 * a laptop several minutes out reporting things overdue that are not — so a
 * locally derived age would disagree with the server's by a day at either end of
 * the day, on a figure a person acts on. `oldestDueDate` is therefore used for
 * DISPLAY only, by {@link overdueTitle}.
 *
 * ## ⚠️ Pluralisation: `pluralise()`, and the three wrong versions
 *
 * "1 day", never "1 days" — this prints beside a customer's name.
 * {@link pluralise} in `@/lib/format` is the house helper and takes **both forms
 * as literals we wrote** (`'day'`, defaulting the plural to `'days'`), which is
 * the property that makes it safe here. It is not a pluraliser: it *selects*
 * between two strings known at authoring time.
 *
 * `src/lib/inclusions.ts` forbids pluralising, and that rule is untouched — it
 * is about **stored, admin-supplied** unit words (`campus` → `campuses`, which
 * follows no rule worth writing), told to us by a database row so that two
 * independent implementations cannot disagree. There is no stored word here and
 * no second implementation; the noun is a constant in this file.
 *
 * The three implementations considered and rejected, each run against the table
 * above:
 *
 * 1. **`${n} days` flat.** Prints **`1 days`** — the exact string
 *    `inclusions.ts` exists to make impossible, on the row a reader is most
 *    likely to be looking at, since a debt one day old is the one somebody just
 *    noticed. Passes every case except `daysOverdue` 1.
 * 2. **`${n} day${n === 1 ? '' : 's'}` inline.** Correct for *this* noun and
 *    wrong as a shape: it is a suffix RULE rather than a choice between two
 *    written words, so the next person to copy the line onto an irregular noun
 *    gets `1 companys`, and it duplicates a helper `formatDuration` already
 *    uses for this very noun. Rejected for what it teaches, not for what it
 *    prints.
 * 3. **`Intl.PluralRules` / `toLocaleString()`.** Follows the VIEWER's locale,
 *    so this sentence would pluralise and group its digits differently for
 *    different staff while the rest of the page stayed in English — and
 *    `toLocaleString` on the amount would mean a float. `format.ts` already
 *    refuses `Intl.ListFormat` for the first reason and `Number()` for the
 *    second.
 *
 * A fourth is worth naming because it is the one this codebase has actually
 * shipped wrong: **`n === 1 && singular ? singular : plural`** — *"prefer the
 * singular when we have one"* (fixture row `I13` in `line-description.ts`). It
 * cannot fire here, and the reason is the whole argument for using
 * {@link pluralise}: both forms are non-null literals, so there is no
 * half-configured state for a fallback to paper over.
 */

import { formatDateOnly, formatDecimalString, pluralise } from './format';
// A TYPE import: erased at build time, so this module gains no runtime
// dependency on the API layer and stays as pure as it was when it declared the
// shape itself. See `OverdueLabelInput` for why the declaration went.
import type { OverdueSummary as WireOverdueSummary } from './types';

/**
 * What this wording is allowed to read: how much is overdue, in which
 * currencies, and since when.
 *
 * ⚠️ **`totals` is a LIST because a company can be overdue in more than one
 * currency**, and the list is the whole point — see this file's header. It is
 * never collapsed to one figure and the caller must not do so either.
 *
 * ## 🔴 DERIVED FROM THE WIRE TYPE WITH `Pick`, NEVER RE-DECLARED
 *
 * *SUPERSEDED, 2026-09-02: a second `interface OverdueSummary` was declared here
 * with the three fields spelled out, on the reasoning that a narrower render
 * input keeps this module pure and that TypeScript's structural typing lets a
 * wire row assign into it with no cast.*
 *
 * **Every word of that was true, and it is exactly why it had to go.** The
 * agent that wrote it also wrote down what it would cost, which is the finding:
 * a narrower structural type **keeps accepting the wider one for ever**. If
 * `daysOverdue` became nullable, or `totals` grew a summed convenience field,
 * or a member changed meaning, **nothing would fail** — no build error, no test,
 * no render. Two copies that agree the day they are written, on money.
 *
 * `Pick` keeps the whole benefit and removes the cost. This is still the three
 * fields the wording needs and nothing more, so the module still cannot reach
 * for a company name or an id — but the three now FOLLOW `types.ts`, so a change
 * there lands here as a `tsc` error rather than as a silently stale duplicate.
 *
 * ⚠️ **Do not "restore purity" by re-declaring the shape.** The import is a type
 * import: it is erased at build time and pulls no runtime dependency on the API
 * layer, so nothing is actually gained by the copy.
 */
export type OverdueLabelInput = Pick<
  WireOverdueSummary,
  'oldestDueDate' | 'daysOverdue' | 'totals'
>;

/**
 * One currency's figure — `GBP 12,400.00` — or `null` when the row carries no
 * figure at all.
 *
 * A row is skipped only when its **amount or its code is blank**, because
 * `formatDecimalString('')` renders an em dash and `GBP —` reads as a rendering
 * fault rather than as a debt. ⚠️ **A row is never skipped on its VALUE.**
 * `'0.00'` is a figure somebody recorded and prints as one; testing an amount
 * against zero would need the float this module refuses, and "drop the small
 * ones" is the forbidden shortcut in this file's header wearing a tidier hat.
 */
/*
 * ⚠️ `currency` IS NULLABLE ON THE WIRE, and this function was written against
 * a `string`. `d_invoices.currency` is `CHAR(3) DEFAULT NULL`, so an invoice can
 * carry a balance and no code; the server emits those rather than dropping them,
 * because dropping hides money somebody is owed.
 *
 * ⚠️ **NOTHING CAUGHT THIS UNTIL THE INPUT TYPE WAS DERIVED FROM THE WIRE TYPE
 * WITH `Pick`.** While this module declared its own narrower copy of the shape,
 * a `null` arrived typed as a `string` and `.trim()` on it was a runtime throw
 * that `tsc` called safe — the whole reason the duplicate went.
 *
 * A missing code is treated exactly as a blank one: the entry is skipped, never
 * printed as `null 12,400.00` and never coalesced into a code-less figure.
 */
function currencyTotal(total: OverdueLabelInput['totals'][number]): string | null {
  const code = (total.currency ?? '').trim();
  const amount = total.amount.trim();
  if (code === '' || amount === '') return null;
  return `${code} ${formatDecimalString(amount)}`;
}

/**
 * The days clause — `45 days`, `1 day` — or `null` when the count cannot be
 * stated.
 *
 * ⚠️ **`0`, a negative, a fraction and a non-finite number all return `null`,
 * and the caller then prints the money WITHOUT an age.** None of them should
 * occur — overdue means past due, and the server sends a whole number of days —
 * but "should not occur" is not a plan, and the two failure directions are not
 * equal:
 *
 * - Printing them gives `overdue, 0 days` (which says the opposite of overdue),
 *   `overdue, -3 days`, or `overdue, 45.5 days` — a figure that looks
 *   authoritative and is not.
 * - Suppressing the whole chip would **hide real money because a derived count
 *   disagreed**, which is the worse half by a distance.
 *
 * So the money survives and only the nonsense clause is dropped: fail towards
 * the visible correct half. Nothing is rounded or clamped here — inventing a
 * rounding would be a second answer to a question the server already answered.
 */
function daysClause(daysOverdue: number): string | null {
  if (!Number.isInteger(daysOverdue) || daysOverdue < 1) return null;
  return pluralise(daysOverdue, 'day');
}

/**
 * The wording that goes inside the bracket, or **`null` when there is nothing
 * to say**.
 *
 * ```
 * GBP 12,400.00 overdue, 45 days
 * GBP 12,400.00 + USD 3,000.00 overdue, 45 days
 * GBP 12,400.00 overdue                          // an unusable day count
 * ```
 *
 * ⚠️ **`null` is the empty-`totals` answer and the caller must render NOTHING
 * for it** — not an empty bracket, not `( overdue, 45 days)`, not a dash. A
 * company with nothing overdue is the ordinary case on this dashboard, and a
 * stray bracket beside its name reads as a fault in the row.
 *
 * The brackets themselves are **not** in this string. The caller owns them, so
 * the same wording can go in a `title`, an `aria-label` or an export without
 * arriving pre-punctuated for one of them.
 */
export function overdueLabel(summary: OverdueLabelInput): string | null {
  const figures = summary.totals
    .map(currencyTotal)
    .filter((figure): figure is string => figure !== null);
  if (figures.length === 0) return null;

  /*
   * ' + ' and not ', ' or ' and ': see the header. The plus is what says these
   * two quantities have deliberately NOT been added together.
   */
  const money = `${figures.join(' + ')} overdue`;
  const days = daysClause(summary.daysOverdue);
  return days === null ? money : `${money}, ${days}`;
}

/**
 * The same wording plus the date the balance has been overdue **since** — for a
 * `title`, where the visible chip may be truncated.
 *
 * ```
 * GBP 12,400.00 + USD 3,000.00 overdue, 45 days. Oldest due 12 Jan 2026.
 * ```
 *
 * ⚠️ **This is a VALUE, not helper text.** The standing prohibition is on advice
 * and explanation; what belongs in a `title` is a fact the reader would
 * otherwise have to go and look up — the same exception a truncated table cell
 * already relies on, and the one `Badge`'s own `title` prop documents. Nothing
 * here explains the chip, and nothing may be added that does.
 *
 * ⚠️ **A `title` is invisible on touch**, so nothing that lives only here may be
 * something a reader NEEDS. The money and the age are both in
 * {@link overdueLabel}; only the exact date is extra.
 *
 * `null` on the same condition as {@link overdueLabel}, so a caller cannot end
 * up with a tooltip on a chip that is not there.
 */
export function overdueTitle(summary: OverdueLabelInput): string | null {
  const label = overdueLabel(summary);
  if (label === null) return null;

  /*
   * `formatDateOnly` builds the date in UTC at both ends rather than parsing it
   * locally, which is what stops '2026-05-31' printing as 30 May west of
   * Greenwich. It answers an em dash for an unusable value, and an em dash in
   * the middle of a sentence reads as a fault, so the clause is dropped instead.
   */
  const due = formatDateOnly(summary.oldestDueDate);
  return due === '—' ? `${label}.` : `${label}. Oldest due ${due}.`;
}
